import { useContext, useState } from 'react';
import { ViewContext, CatalogDispatchContext, CatalogStateContext } from '../../store/CatalogContext';
import { createSlug } from '../../utils/slugify';
import type { FilterOption, IndividualBundleOption } from '../../types/filters';
import SearchAndSuggestion from './SearchAndSuggestions';
import type { Product } from '../../types/product';
import FiltersSidebarSkeleton from '../loading-skeletons/FiltersSidebarSkeleton';
import AppliedFilters from './AppliedFilters';

interface SearchFiltersSidebar {
  group: 'purchased' | 'related' | 'fullCatalog';
  prefix: string;
  filters: {
    collection: FilterOption[];
    category: FilterOption[];
    subCategory: FilterOption[];
    individualBundle: IndividualBundleOption[];
    showBundleFilters?: boolean;
  };
  filteredProducts: Product[];
}

export default function SearchFiltersSidebar({ group, prefix, filters, filteredProducts }: SearchFiltersSidebar) {
  const viewState = useContext(ViewContext);
  const catalogDispatch = useContext(CatalogDispatchContext);
  const catalogState = useContext(CatalogStateContext); // only for global flags (isLoading)

  const currentSort = viewState[group].sort;

  const [showAllFilters, setShowAllFilters] = useState({
    collection: false,
    category: false,
    subCategory: false,
  });

  const filterLimit = 10;

  const persistView = (updatedView: object) => {
    sessionStorage.setItem(
      'catalogView',
      JSON.stringify({
        ...viewState,
        ...updatedView,
      })
    );
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSort = e.target.value as 'default' | 'a-z' | 'z-a';
    const updatedView = {
      [group]: {
        ...viewState[group],
        sort: currentSort === newSort ? 'default' : newSort,
      },
    };

    catalogDispatch({
      type: 'VIEW',
      payload: updatedView,
    });

    persistView(updatedView);
  };

  const handleShowAllFilters = (filterType: keyof typeof showAllFilters) => {
    setShowAllFilters((prev) => ({ ...prev, [filterType]: !prev[filterType] }));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked, dataset } = e.target;
    const filterType = dataset.filterType as 'collection' | 'category' | 'subCategory' | 'individualBundle';
    const currentFilter = viewState[group].filters[filterType] || [];
    let updatedFilters = [...currentFilter];
    if (checked) updatedFilters.push(value);
    else updatedFilters = updatedFilters.filter((v) => v !== value);

    const updatedView = {
      [group]: {
        ...viewState[group],
        filters: {
          ...viewState[group].filters,
          [filterType]: updatedFilters,
        },
        sort: 'a-z', // resetting sort to default 'a-z' when filters are applied
      },
    };

    catalogDispatch({
      type: 'VIEW',
      payload: updatedView,
    });

    persistView(updatedView);
  };

  const makeFilterList = (
    items: { value: string; total: number; matching: number }[],
    type: 'collection' | 'category' | 'subCategory',
    expanded: boolean
  ) =>
    (expanded ? items : items.slice(0, filterLimit)).map((item) => (
      <div className="form-check mb-2" key={item.value}>
        <input
          className="form-check-input"
          type="checkbox"
          value={item.value}
          id={`${prefix}-${type}-${createSlug(item.value)}`}
          data-filter-type={type}
          onChange={handleFilterChange}
          checked={!!viewState[group].filters[type]?.includes(item.value)}
          disabled={item.matching === 0}
        />
        <label className="form-check-label d-block" htmlFor={`${prefix}-${type}-${createSlug(item.value)}`}>
          {item.value} <span className="text-muted small">({item.total})</span>
        </label>
      </div>
    ));

  const collectionFilters = makeFilterList(filters.collection, 'collection', showAllFilters.collection);
  const categoryFilters = makeFilterList(filters.category, 'category', showAllFilters.category);
  const subCategoryFilters = makeFilterList(filters.subCategory, 'subCategory', showAllFilters.subCategory);

  return (
    <>
      <div className="d-lg-none">
        <AppliedFilters group={group} />
      </div>

      <SearchAndSuggestion group={group} prefix={prefix} filteredProducts={filteredProducts} />

      {catalogState.isLoading ? (
        <FiltersSidebarSkeleton />
      ) : (
        <div className="accordion accordion-filters accordion-flush mt-4 border-0 border-top" id="sidebar-filters">
          {collectionFilters.length > 0 && (
            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#filter-collection"
                  aria-expanded="true"
                  aria-controls="filter-collection"
                >
                  Collection
                </button>
              </h3>
              <div id="filter-collection" className="accordion-collapse show">
                <div className="accordion-body">
                  {collectionFilters}
                  {!showAllFilters.collection && filters.collection.length > filterLimit && (
                    <button
                      className="btn btn-link show-all-btn text-decoration-none p-0 mt-1"
                      type="button"
                      onClick={() => handleShowAllFilters('collection')}
                    >
                      &#43; Show All <span className="text-muted small">({filters.collection.length - filterLimit})</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {categoryFilters.length > 0 && (
            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#filter-category"
                  aria-expanded="true"
                  aria-controls="filter-category"
                >
                  Category
                </button>
              </h3>
              <div id="filter-category" className="accordion-collapse show">
                <div className="accordion-body">
                  {categoryFilters}
                  {!showAllFilters.category && filters.category.length > filterLimit && (
                    <button
                      className="btn btn-link show-all-btn text-decoration-none p-0 mt-1"
                      type="button"
                      onClick={() => handleShowAllFilters('category')}
                    >
                      &#43; Show All <span className="text-muted small">({filters.category.length - filterLimit})</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {subCategoryFilters.length > 0 && (
            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#filter-subcategory"
                  aria-expanded="true"
                  aria-controls="filter-subcategory"
                >
                  Sub-Category
                </button>
              </h3>
              <div id="filter-subcategory" className="accordion-collapse show">
                <div className="accordion-body">
                  {subCategoryFilters}
                  {!showAllFilters.subCategory && filters.subCategory.length > filterLimit && (
                    <button
                      className="btn btn-link show-all-btn text-decoration-none p-0 mt-1"
                      type="button"
                      onClick={() => handleShowAllFilters('subCategory')}
                    >
                      &#43; Show All <span className="text-muted small">({filters.subCategory.length - filterLimit})</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {filters?.showBundleFilters && (
            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#bundle-package"
                  aria-expanded="true"
                  aria-controls="bundle-package"
                >
                  Bundle/Package
                </button>
              </h3>
              <div id="bundle-package" className="accordion-collapse show">
                <div className="accordion-body">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value="Individual Course"
                      id={`${prefix}-individual-products`}
                      data-filter-type="individualBundle"
                      onChange={handleFilterChange}
                      checked={viewState[group].filters.individualBundle?.includes('Individual Course')}
                    />
                    <label className="form-check-label d-block" htmlFor={`${prefix}-individual-products`}>
                      Individual Course
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value="Bundle/Package"
                      id={`${prefix}-bundle-products`}
                      data-filter-type="individualBundle"
                      onChange={handleFilterChange}
                      checked={viewState[group].filters.individualBundle?.includes('Bundle/Package')}
                    />
                    <label className="form-check-label d-block" htmlFor={`${prefix}-bundle-products`}>
                      Bundle/Package
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="accordion-item d-sm-none border-bottom">
            <h3 className="accordion-header">
              <button
                className="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#sort-filter"
                aria-expanded="true"
                aria-controls="sort-filter"
              >
                Sort
              </button>
            </h3>
            <div id="sort-filter" className="accordion-collapse show">
              <div className="accordion-body">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value="a-z"
                    id={`${prefix}-a-z`}
                    checked={currentSort === 'a-z'}
                    onChange={handleSortChange}
                  />
                  <label className="form-check-label d-block" htmlFor={`${prefix}-a-z`}>
                    A-Z
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value="z-a"
                    id={`${prefix}-z-a`}
                    checked={currentSort === 'z-a'}
                    onChange={handleSortChange}
                  />
                  <label className="form-check-label d-block" htmlFor={`${prefix}-z-a`}>
                    Z-A
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
